/*
  Единственная точка обращения к серверу.

  Ошибка сервера приходит в одном виде — что произошло, чем грозит, что делать
  сейчас (§4.5.6), — и здесь превращается в объект той же формы. Экран обязан
  показать все три части: сообщение без третьей считается недоделанной
  функцией, и придумывать её на клиенте нельзя.

  Сессия живёт в cookie, недоступной сценариям страницы, поэтому токен нигде
  не хранится и не передаётся вручную: браузер прикладывает его сам.
*/

import { notifySessionLost } from './session.js'

const BASE = '/api/v1'

export class ApiError extends Error {
  constructor({ status, code, message, remedy, details }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.remedy = remedy
    this.details = details ?? {}
  }
}

const NETWORK_ERROR = {
  code: 'network',
  message: 'Не удалось связаться с сервером.',
  remedy: 'Проверьте подключение к сети и повторите. Если это повторяется, сообщите в поддержку.',
}

/*
  Чтения, которые уже в пути (02.10.2026, жалоба владельца «страницы
  подтормаживают»). Замер стенда: каждый запрос кабинета уходил дважды - режим
  разработки React нарочно запускает загрузку экрана два раза, а несколько
  блоков одной страницы спрашивают одно и то же (названия участков - и фильтр,
  и таблица). Сервер стенда работает одним процессом, второй запрос ждал
  первого, и реестр открывался 0,5 с вместо 0,3.

  Одинаковое чтение, пока ответ на первое в пути, ждёт тот же ответ, а не
  уходит на сервер вторым. Готовый ответ не хранится: следующее чтение идёт
  заново, поэтому устаревшую цифру этот приём не покажет. Изменяющие запросы
  не склеиваются никогда - два нажатия «отправить» должны быть двумя.

  23.10.2026. Склеивается только чтение, ушедшее через ТОТ ЖЕ `fetch`. В кабинете он
  один на всю жизнь страницы, а проверки подставляют свой поддельный на каждый тест,
  и перечень чтений в пути живёт в модуле между тестами: недочитанный запрос прошлого
  теста перехватывал такое же чтение следующего, и вкладка «Файлы» показывала «Не
  удалось связаться с сервером» вместо списка - через раз, в зависимости от скорости
  машины. Поведение кабинета от этого не меняется.
*/
const reading = new Map()

function request(path, options = {}) {
  const { method = 'GET', params, headers, credentials } = options
  if (method !== 'GET') return send(path, options)
  // Заголовки и способ передачи сессии входят в ключ: кабинет должника ходит
  // своим пропуском в заголовке, и его чтение с чтением кабинета УК не склеится.
  const key = JSON.stringify([path, params ?? null, headers ?? null, credentials ?? null])
  const pending = reading.get(key)
  // Ждущему - своя копия: правка ответа одним экраном не задевает другой.
  if (pending && pending.via === globalThis.fetch) {
    return pending.started.then((payload) => structuredClone(payload))
  }
  const entry = { via: globalThis.fetch, started: null }
  // Убирает из перечня только СВОЮ запись: место могло достаться чтению через другой `fetch`.
  entry.started = send(path, options).finally(() => {
    if (reading.get(key) === entry) reading.delete(key)
  })
  reading.set(key, entry)
  return entry.started
}

async function send(path, { method = 'GET', body, params, formData, headers, credentials } = {}) {
  const url = new URL(BASE + path, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value)
      }
    })
  }

  let response
  try {
    response = await fetch(url, {
      method,
      credentials: credentials ?? 'same-origin',
      // Пустой набор заголовков остаётся неопределённым, а не пустым объектом:
      // «кабинет не посылает ничего своего» — проверяемое свойство, и пустой
      // объект отличить от его нарушения уже нельзя.
      headers: buildHeaders({ formData, body, headers }),
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
    })
  } catch (cause) {
    throw new ApiError({ status: 0, ...NETWORK_ERROR, details: { cause: String(cause) } })
  }

  if (response.status === 204) return null

  const payload = await readBody(response)
  if (!response.ok) {
    sessionEnded(response.status, path, headers || credentials)
    throw new ApiError({
      status: response.status,
      code: payload?.code ?? 'unexpected',
      message: payload?.message ?? describeStatus(response.status),
      remedy: payload?.remedy ?? 'Повторите действие. Если это повторяется, сообщите в поддержку.',
      details: payload?.details,
    })
  }
  return payload
}

/*
  Ответ 401 на запрос человека, который уже вошёл, - сессия кончилась посреди работы
  (аудит 28.10.2026, раздел 5.3): каркас возвращает его на вход, а не оставляет экраны с
  надписью «войдите заново» без кнопки входа. Не считаются: неверный пароль при входе
  (тоже 401) и кабинет должника, который ходит своим пропуском, а не сессией сотрудника.
*/
function sessionEnded(status, path, ownPass) {
  if (status === 401 && !ownPass && !path.startsWith('/auth/login')) notifySessionLost()
}

function buildHeaders({ formData, body, headers }) {
  const assembled = {
    ...(formData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...(headers ?? {}),
  }
  return Object.keys(assembled).length ? assembled : undefined
}

async function readBody(response) {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

async function download(path, { params, filename }) {
  // Сохранение делается ссылкой на объект в памяти: адрес с сессией в cookie
  // браузер открыл бы в соседней вкладке, а имя файла потерял.
  const blob = await fetchBlob(path, params)
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)
}

async function fetchBlob(path, params) {
  // Файл идёт мимо разбора JSON, но по тем же правилам: отказ сервера обязан
  // стать ApiError, иначе экран покажет «скачано» на пустоте. Отдельно от
  // `download` с 06.10.2026: запись разговора не сохраняется, а играется.
  const url = new URL(BASE + path, window.location.origin)
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value)
  })

  let response
  try {
    response = await fetch(url, { credentials: 'same-origin' })
  } catch (cause) {
    throw new ApiError({ status: 0, ...NETWORK_ERROR, details: { cause: String(cause) } })
  }
  if (!response.ok) {
    const payload = await readBody(response)
    sessionEnded(response.status, path)
    throw new ApiError({
      status: response.status,
      code: payload?.code ?? 'unexpected',
      message: payload?.message ?? describeStatus(response.status),
      remedy: payload?.remedy ?? 'Повторите действие. Если это повторяется, сообщите в поддержку.',
      details: payload?.details,
    })
  }
  return response.blob()
}

export { fieldErrors } from './fieldErrors.js'

function describeStatus(status) {
  // Запасной путь: обычно проверка тела запроса приходит в общем виде ошибки,
  // с причиной по каждому полю. Эта фраза остаётся на случай пустого тела
  // ответа - например когда отказ пришёл не от приложения, а от посредника.
  if (status === 422) return 'Данные в форме заполнены не полностью или неверно.'
  if (status === 401) return 'Сессия закончилась.'
  if (status === 403) return 'Это действие недоступно вашей роли.'
  if (status === 404) return 'Не найдено.'
  return 'Сервер ответил ошибкой.'
}

/*
  Файл, присланный заранее (19.10.2026): `{ storedId, name, size }` вместо
  самого файла. Указание владельца - файл уходит на сервер один раз и
  разбирается один раз; все следующие действия называют его номером. Формы ниже
  сами решают, что приложить: номер или файл, - и экраны о разнице не знают.
*/
function appendFile(form, field, file) {
  if (file?.storedId) form.append(`${field}_id`, file.storedId)
  else form.append(field, file)
}

function importForm(file, { force = false, mapping = null, source = null, only = null } = {}) {
  // Сопоставление уходит парами «поле системы» - «колонка файла»; пустое
  // значение означает «такой колонки нет» и снимает привязку на сервере.
  const form = new FormData()
  appendFile(form, 'file', file)
  if (force) form.append('force', 'true')
  if (mapping) form.append('column_overrides', JSON.stringify(mapping))
  // Счета, отмеченные в окне «Какие счета загрузить» (16.10.2026). Не выбирали -
  // поле не уходит вовсе, и сервер грузит весь файл.
  if (only) form.append('only_accounts', JSON.stringify(only))
  // Откуда файл: банковская выписка или реестр оплат. Только для поступлений -
  // у выгрузки начислений источник один.
  if (source) form.append('source', source)
  return form
}

function registryForm(file, mapping, treatments, only) {
  const form = importForm(file, { mapping, only })
  if (treatments && Object.keys(treatments).length) {
    form.append('sheet_treatments', JSON.stringify(treatments))
  }
  return form
}

/*
  Выгрузка 1С: начисления обязательны, реквизиты счетов - нет. Пустое поле файла
  не прикладывается вовсе, а не шлётся пустым: сервер отличает «второго файла не
  дали» от «дали пустой», и во втором случае должен сказать об этом человеку.
*/
function onecForm(charges, details, force = false, only = null) {
  const form = new FormData()
  appendFile(form, 'charges', charges)
  if (details) appendFile(form, 'details', details)
  if (force) form.append('force', 'true')
  // Счета из окна «Какие счета загрузить» (16.10.2026) - как у `importForm`.
  if (only) form.append('only_accounts', JSON.stringify(only))
  return form
}

/*
  Отправка файла загрузки с ходом «отправлено N%» (19.10.2026). Через
  XMLHttpRequest, а не `fetch`: `fetch` хода отправки не сообщает, а большой
  файл уходит секундами, и без полосы экран снова неотличим от зависания.
  Ошибка - тем же `ApiError`, что у остальных запросов.
*/
function sendWithProgress(path, form, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', BASE + path)
    xhr.withCredentials = true
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total)
    }
    xhr.onerror = () => reject(new ApiError({ status: 0, ...NETWORK_ERROR, details: {} }))
    xhr.onload = () => {
      let payload = null
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null
      } catch {
        payload = null
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(payload)
      sessionEnded(xhr.status, path)
      return reject(
        new ApiError({
          status: xhr.status,
          code: payload?.code ?? 'unexpected',
          message: payload?.message ?? describeStatus(xhr.status),
          remedy:
            payload?.remedy ?? 'Повторите действие. Если это повторяется, сообщите в поддержку.',
          details: payload?.details,
        }),
      )
    }
    xhr.send(form)
  })
}

/*
  Скан протокола собрания (21.10.2026, макет 5А): чтение только предлагает значения и
  ничего не сохраняет, файл уходит на сервер ещё раз вместе со сверенными значениями;
  замена уже внесённого протокола - только с `replace`. `base` - путь дома: через
  участок или, с 02.11.2026, сам по себе (`/court-houses/{id}`).
*/
function readScanAt(base, file) {
  const form = new FormData()
  form.append('file', file)
  return request(`${base}/protocol-scan/reading`, { method: 'POST', formData: form })
}

function saveScanAt(base, file, values, replace) {
  const form = new FormData()
  form.append('file', file)
  form.append('protocol_number', values.protocol_number ?? '')
  form.append('protocol_date', values.protocol_date ?? '')
  form.append('management_method', values.management_method ?? '')
  form.append('organisation', values.organisation ?? '')
  form.append('replace', replace ? 'true' : 'false')
  return request(`${base}/protocol-scan`, { method: 'PUT', formData: form })
}

export const api = {
  // Файл загрузки - один раз (19.10.2026): отправка с ходом, чтение фоновой
  // работой (ход - `importFile`), скачивание из журнала, запись фоновой работой.
  sendImportFile: (kind, file, onProgress) => {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    return sendWithProgress('/imports/files', form, onProgress)
  },
  importFile: (id) => send(`/imports/files/${id}`),
  downloadImportFile: (id, filename) => download(`/imports/files/${id}/content`, { filename }),
  startImportJob: (body) => send('/imports/jobs', { method: 'POST', body }),
  importJob: (id) => send(`/imports/jobs/${id}`),

  login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    request('/auth/password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),

  dashboard: (asOf) => request('/dashboard', { params: { as_of: asOf } }),
  // Числа для значков в меню. Отдельно от экранов: значок виден из любого
  // раздела, а раздел, к которому он относится, может быть не открыт.
  badges: () => request('/badges'),
  registry: (params) => request('/registry', { params }),
  // «Выбрать весь отбор» (решение владельца 23.10.2026): весь отбор без страницы, не больше
  // предела одного действия; `{ items, total, ceiling }`.
  registrySelection: (params) => request('/registry/selection', { params }),
  account: (id, asOf) => request(`/accounts/${id}`, { params: { as_of: asOf } }),
  // Лента движения дела. Отдельно от карточки намеренно: карточке нужны триаж,
  // состав должников и справочник участков, ленте - семь других запросов, и ни
  // один из них не нужен, чтобы показать сумму долга. Так карточка открывается
  // сразу, а лента приходит следом.
  accountTimeline: (id, params) => request(`/accounts/${id}/timeline`, { params }),
  // Как сформировался долг: сальдо, начисления и оплаты по месяцам и услугам
  // (05.10.2026). Своя страница, поэтому и свой запрос.
  debtFormation: (id) => request(`/accounts/${id}/debt-formation`),
  changeStage: (id, target, reason) =>
    request(`/accounts/${id}/stage`, { method: 'POST', body: { target, reason } }),
  // Ввод данных руками на карточке счёта (20.10.2026): счёт, начисления, оплаты,
  // должники и проживающие вносятся, правятся и убираются. Отказ сервера уходит
  // в окно, где человек нажал кнопку (правило 3 скилла `frontend`).
  createAccount: (body) => request('/accounts', { method: 'POST', body }),
  // Правка номера и адреса самого счёта (21.10.2026): только то, что человек изменил.
  editAccount: (id, body) => request(`/accounts/${id}`, { method: 'PATCH', body }),
  // Страница начислений (серверная пагинация, 21.10.2026): { items, total, limit, offset }.
  // `params` - { limit, offset } и при необходимости { with_debt: true }.
  charges: (id, params) => request(`/accounts/${id}/charges`, { params }),
  addCharge: (id, body) => request(`/accounts/${id}/charges`, { method: 'POST', body }),
  editCharge: (id, chargeId, body) =>
    request(`/accounts/${id}/charges/${chargeId}`, { method: 'PATCH', body }),
  removeCharge: (id, chargeId) =>
    request(`/accounts/${id}/charges/${chargeId}`, { method: 'DELETE' }),
  // Макет 6А «сделать все три» (23.10.2026): итог по всему счёту, ряд месяцев и вставка
  // из Excel. Итог и расхождения с 1С считает сервер; вставку разбирает тоже он.
  chargeTotals: (id) => request(`/accounts/${id}/charge-totals`),
  addChargeSeries: (id, body) =>
    request(`/accounts/${id}/charges/series`, { method: 'POST', body }),
  previewChargePaste: (id, text) =>
    request(`/accounts/${id}/charges/paste/preview`, { method: 'POST', body: { text } }),
  addChargePaste: (id, text) =>
    request(`/accounts/${id}/charges/paste`, { method: 'POST', body: { text } }),
  payments: (id, params) => request(`/accounts/${id}/payments`, { params }),
  addPayment: (id, body) => request(`/accounts/${id}/payments`, { method: 'POST', body }),
  reversePayment: (id, paymentId) =>
    request(`/accounts/${id}/payments/${paymentId}`, { method: 'DELETE' }),
  participants: (id) => request(`/accounts/${id}/participants`),
  addParticipant: (id, body) => request(`/accounts/${id}/participants`, { method: 'POST', body }),
  editParticipant: (id, linkId, body) =>
    request(`/accounts/${id}/participants/${linkId}`, { method: 'PATCH', body }),
  removeParticipant: (id, linkId) =>
    request(`/accounts/${id}/participants/${linkId}`, { method: 'DELETE' }),
  residents: (id, params) => request(`/accounts/${id}/residents`, { params }),
  // Документы счёта - страница (21.10.2026): карточка несёт только `documents_total`.
  accountDocuments: (id, params) => request(`/accounts/${id}/documents`, { params }),
  addResident: (id, body) => request(`/accounts/${id}/residents`, { method: 'POST', body }),
  editResident: (id, residentId, body) =>
    request(`/accounts/${id}/residents/${residentId}`, { method: 'PATCH', body }),
  removeResident: (id, residentId) =>
    request(`/accounts/${id}/residents/${residentId}`, { method: 'DELETE' }),
  buildPackage: (id, courtName, asOf) =>
    request(`/accounts/${id}/documents`, {
      method: 'POST',
      body: { court_name: courtName || null, account_ids: [] },
      params: { as_of: asOf },
    }),
  // Досудебное уведомление (20.10.2026): по счёту - из карточки, по выбранным -
  // из реестра. Ссылка на страницу должника печатается в самом уведомлении.
  buildNotice: (id, asOf) =>
    request(`/accounts/${id}/notices`, { method: 'POST', params: { as_of: asOf } }),
  buildNotices: (accountIds, asOf) =>
    request('/notices', {
      method: 'POST',
      body: { account_ids: accountIds },
      params: { as_of: asOf },
    }),
  // Кому и как предъявляется требование (26.10.2026, решение владельца): окно у
  // сборки пакета. Итог выбора считает сервер тем же ядром, что соберёт пакет.
  claimChoice: (id, asOf) => request(`/accounts/${id}/claim-choice`, { params: { as_of: asOf } }),
  previewClaimChoice: (id, body) =>
    request(`/accounts/${id}/claim-choice/preview`, { method: 'POST', body }),
  saveClaimChoice: (id, body) => request(`/accounts/${id}/claim-choice`, { method: 'PUT', body }),

  // Правка сведений о должнике - по ЧЕЛОВЕКУ, а не по счёту (17.09.2026). Один
  // и тот же человек бывает должником по нескольким счетам сразу, и адрес
  // «счёт → его должник» обещал бы, что правка касается только этого счёта.
  savePerson: (personId, body) => request(`/persons/${personId}`, { method: 'PATCH', body }),

  // Подтверждение состава должников, пришедшего обменом с 1С (21.09.2026).
  // По счёту, а не по человеку: подтверждается не запись о лице, а то, что
  // заявлений по этому счёту будет именно столько.
  confirmParticipants: (accountId) =>
    request(`/accounts/${accountId}/participants/confirm`, { method: 'POST' }),

  // Сводка конвейера: четыре блока и готовность разбора. Очередь к подаче листается
  // отдельным методом (21.10.2026: страницы режет сервер, `queue_limit` убран).
  conveyor: (asOf) => request('/conveyor', { params: { as_of: asOf } }),
  // `params` - окно (`limit`, `offset`); ответ - `{ items, total, limit, offset }`.
  conveyorQueue: (params) => request('/conveyor/queue', { params }),
  conveyorQueueSelection: () => request('/conveyor/queue/selection'),
  markFiled: (accountIds) =>
    request('/conveyor/filed', { method: 'POST', body: { account_ids: accountIds } }),
  waveRegistry: (accountIds, courtName, asOf) =>
    request('/conveyor/package', {
      method: 'POST',
      body: { account_ids: accountIds, court_name: courtName || null },
      params: { as_of: asOf },
    }),
  // 23.10.2026: страница `{ items, total }` по окну `{ limit, offset }`, а не двадцать последних.
  waveRegistries: (params) => request('/conveyor/registries', { params }),

  // Действия над выбранными в реестре счетами (16.10.2026). Удаление - двумя
  // обращениями: разбор для окна подтверждения и само удаление, которое
  // считает разбор заново.
  removalReview: (accountIds) =>
    request('/accounts/removal/review', { method: 'POST', body: { account_ids: accountIds } }),
  removeAccounts: (accountIds) =>
    request('/accounts/removal', { method: 'POST', body: { account_ids: accountIds } }),
  changeStages: (accountIds, target, reason) =>
    request('/accounts/stage', {
      method: 'POST',
      body: { account_ids: accountIds, target, reason },
    }),
  conveyorChosen: (accountIds) =>
    request('/conveyor/chosen', { method: 'POST', body: { account_ids: accountIds } }),
  // Удаление собранного по ошибке документа (14.09.2026). Отказ приходит
  // обычным ApiError: счёт, отмеченный поданным, документов не отдаёт.
  deleteDocument: (id) => request(`/documents/${id}`, { method: 'DELETE' }),

  // Справочник судебных участков - §3.4.2. Участок подтягивается к заявлению по
  // адресу помещения, и ручное поле участка осталось запасным путём для
  // нетипового случая (решение владельца 11.09.2026).
  // Суды страницей (21.10.2026, серверная пагинация): `params` - { limit, offset, kind }.
  // Дома участка и дома без участка - отдельные постраничные чтения ниже: в строке
  // суда их больше нет, у крупного участка их сотни.
  courtSections: (params) => request('/court-sections', { params }),
  courtSectionBuildings: (id, params) => request(`/court-sections/${id}/buildings`, { params }),
  // `params` - { q, limit, offset }: поиск по адресу идёт по всему списку на сервере.
  unassignedHouses: (params) => request('/court-sections/unassigned-houses', { params }),
  unassignedSummary: () => request('/court-sections/unassigned-summary'),
  // Лёгкий список для поля «Судебный участок»: только номер и наименование.
  // Полный справочник считает покрытие портфеля перебором адресов всех счетов,
  // и поле выбора платило бы за это при каждом открытии карточки (14.09.2026).
  courtSectionNames: () => request('/court-sections/names'),
  courtSectionPreview: (address) => request('/court-sections/preview', { params: { address } }),
  createCourtSection: (body) => request('/court-sections', { method: 'POST', body }),
  updateCourtSection: (id, body) => request(`/court-sections/${id}`, { method: 'PATCH', body }),
  deleteCourtSection: (id) => request(`/court-sections/${id}`, { method: 'DELETE' }),
  // `extra` - полный адрес дома и число помещений, которое видел человек
  // (01.10.2026): без первого сервер не примет дом без населённого пункта, по
  // второму откажет, если портфель изменился, пока страница была открыта.
  attachBuilding: (id, address, management = {}, extra = {}) =>
    request(`/court-sections/${id}/buildings`, {
      method: 'POST',
      body: { address, management, ...extra },
    }),
  detachBuilding: (id, buildingId) =>
    request(`/court-sections/${id}/buildings/${buildingId}`, { method: 'DELETE' }),
  // Основание управления правится отдельно: дома заводились до 14.09.2026, и
  // отвязывать дом ради внесения протокола значило бы терять подсудность.
  setBuildingManagement: (id, buildingId, management) =>
    request(`/court-sections/${id}/buildings/${buildingId}`, {
      method: 'PATCH',
      body: management,
    }),
  // Скан протокола собрания - см. `readScanAt` и `saveScanAt` выше.
  readProtocolScan: (id, buildingId, file) =>
    readScanAt(`/court-sections/${id}/buildings/${buildingId}`, file),
  saveProtocolScan: (id, buildingId, file, values, replace = false) =>
    saveScanAt(`/court-sections/${id}/buildings/${buildingId}`, file, values, replace),
  downloadProtocolScan: (id, buildingId, filename) =>
    download(`/court-sections/${id}/buildings/${buildingId}/protocol-scan`, { filename }),

  // Подсудность домов (02.11.2026, вариант А): все дома портфеля с участком и
  // протоколом. `params` - { q, court, settlement, state, limit, offset }. Протокол
  // дома правится путём дома, без участка: участок может быть из общего справочника.
  courtHouses: (params) => request('/court-houses', { params }),
  courtHousesSummary: () => request('/court-houses/summary'),
  claimCourtHouse: (body) => request('/court-houses', { method: 'POST', body }),
  setHouseManagement: (buildingId, management) =>
    request(`/court-houses/${buildingId}`, { method: 'PATCH', body: management }),
  readHouseProtocolScan: (buildingId, file) => readScanAt(`/court-houses/${buildingId}`, file),
  saveHouseProtocolScan: (buildingId, file, values, replace = false) =>
    saveScanAt(`/court-houses/${buildingId}`, file, values, replace),
  downloadHouseProtocolScan: (buildingId, filename) =>
    download(`/court-houses/${buildingId}/protocol-scan`, { filename }),
  // Приложение к пакету счёта: скан протокола дома, в котором лежит помещение.
  accountProtocolScan: (accountId) => request(`/accounts/${accountId}/protocol-scan`),
  downloadAccountProtocolScan: (accountId, filename) =>
    download(`/accounts/${accountId}/protocol-scan/file`, { filename }),

  // Проверка должников счёта по реестру банкротств - служба «Арбитр Сведения»
  // (21.10.2026). Запуск и опрос хода; ничего не записывается, найденное только
  // показывается, а процедуру вносит человек формой банкротства.
  startRegistryCheck: (accountId) =>
    request(`/accounts/${accountId}/bankruptcy-check`, { method: 'POST' }),
  registryCheck: (accountId, checkId) =>
    request(`/accounts/${accountId}/bankruptcy-check/${checkId}`),
  // Второй шаг (23.10.2026): сообщения по карточке, найденной только по ФИО, -
  // по её номеру; карточка обязана быть из ответа проверки `checkId`.
  requestRegistryMessages: (accountId, checkId, guid) =>
    request(`/accounts/${accountId}/bankruptcy-check/${checkId}/debtors/${guid}/messages`, {
      method: 'POST',
    }),
  registryMessages: (accountId, listId) =>
    request(`/accounts/${accountId}/bankruptcy-check/messages/${listId}`),

  // Портфель по домам - отдельный ресурс, а не часть справочника (14.09.2026):
  // справочник отвечает, в какой суд подавать, а здесь - что у нас есть по
  // этому дому. Экран один: вкладка «Дома» внутри справочника.
  // Обе страницы серверные (21.10.2026): `params` - { limit, offset }.
  houses: (params) => request('/houses', { params }),
  houseAccounts: (address, params) =>
    request('/houses/accounts', { params: { address, ...params } }),
  // Чтения текста документа здесь нет намеренно (решение владельца 09.09.2026):
  // документ смотрят в файле, в том виде, в каком он уйдёт адресату. Показ его
  // же сплошным текстом на экране давал второй, непохожий вид одного документа.
  //
  // Имя файла даёт кабинет, а не сервер: в заголовке ответа кириллица требует
  // отдельного кодирования, и браузеры разбирают его по-разному. Здесь же
  // человек получает файл, названный по-русски, - его кладут в папку дела.
  downloadDocument: (id, format, filename) =>
    download(`/documents/${id}/file`, { params: { format }, filename }),

  // Журнал загрузок одного вида файла или всех сразу. Отбор делает сервер, а не
  // кабинет (01.10.2026): журнал отдаётся последними пятьюдесятью строками, и
  // отсев на клиенте показал бы «загрузок ещё не было» в шаге, где файл
  // загружен, - просто потому, что его строка не попала в эти пятьдесят.
  // С 21.10.2026 журнал - серверная страница `{ items, total, limit, offset }`:
  // `params` - окно (`limit`, `offset`); вид файла отбирает сервер до среза.
  imports: (kind, params) => request('/imports', { params: { ...params, kind } }),
  importIssues: (id) => request(`/imports/${id}/issues`),
  // Отложенное погашение (19.10.2026): сторож объёма больше не останавливает
  // загрузку, а при выгрузке «в разы меньше прошлой» ждёт решения человека.
  decideDeferred: (id, settle) =>
    request(`/imports/${id}/deferred-settlement`, { method: 'POST', body: { settle } }),

  // Чего портфелю не хватает для судебного пакета (18.09.2026). Перечень
  // требований и состояние каждого считает сервер тем же, чем проверяет себя
  // сборщик пакета: свой список обязательного на клиенте разошёлся бы с ним.
  readiness: () => request('/portfolio/readiness'),
  // Предпросмотр и загрузка шлют один и тот же файл: между ними он остаётся в
  // браузере, а не лежит у нас. Хранить чужую выгрузку с персональными данными
  // ради двух минут работы значило бы завести ещё одно место, откуда её нужно
  // вовремя убирать.
  previewImport: (file, mapping) =>
    request('/imports/preview', { method: 'POST', formData: importForm(file, { mapping }) }),

  // `only` - счета из окна выбора (16.10.2026); пусто - весь файл.
  upload: (file, force, mapping, only = null) =>
    request('/imports', { method: 'POST', formData: importForm(file, { force, mapping, only }) }),

  // Реестр должников - шестой вид файла и ПЕРВЫЙ по порядку работы (30.09.2026).
  // Он заводит лицевые счета и должников; суммы по месяцам приходят следующим
  // шагом, отчётом по начислениям. Денег из реестра не берём: «Долг всего» и
  // «Можно взыскать» посчитала управляющая компания на дату выгрузки, а давность
  // и пени считает правовое ядро. Сервер отдаёт их отдельными полями `declared`.
  // `treatments` - выбор человека «лист - способ загрузки» (05.10.2026); лист,
  // которого в выборе нет, грузится способом, предложенным сервером.
  previewRegistry: (file, mapping, treatments, only = null) =>
    request('/imports/registry/preview', {
      method: 'POST',
      formData: registryForm(file, mapping, treatments, only),
    }),

  uploadRegistry: (file, mapping, treatments, only = null) =>
    request('/imports/registry', {
      method: 'POST',
      formData: registryForm(file, mapping, treatments, only),
    }),
  // Судебная работа управляющей компании на карточке счёта (плашка Б1, 05.10.2026).
  customerWork: (id) => request(`/accounts/${id}/customer-work`),
  // Таблица «Дела по счёту» (В2, 06.10.2026): дела УК и наши, не предъявленные месяцы.
  accountCases: (id) => request(`/accounts/${id}/cases`),
  clearHold: (id, reason) =>
    request(`/accounts/${id}/hold/clear`, { method: 'POST', body: { reason } }),
  setCaseMonths: (id, caseId, months) =>
    request(`/accounts/${id}/cases/${caseId}/months`, { method: 'PUT', body: months }),
  // Суд вернул наше заявление (26.10.2026): ответ - обновлённая таблица дел.
  returnFiling: (id, caseId, facts) =>
    request(`/accounts/${id}/cases/${caseId}/returned`, { method: 'POST', body: facts }),
  // Плашка «Счёт ждёт человека» (06.10.2026): вернуть в автоматику с выводом сотрудника.
  returnToAutomation: (id, comment) =>
    request(`/accounts/${id}/contact-state/return`, { method: 'POST', body: { comment } }),

  // Выгрузка 1С - два файла вместо одного: начисления по услугам и реквизиты
  // счетов. Второй необязателен: если счета уже заведены, адрес берётся из
  // портфеля. Склейку делает сервер, экран о ней не знает.
  previewOnec: (charges, details, only = null) =>
    request('/imports/onec/preview', {
      method: 'POST',
      formData: onecForm(charges, details, false, only),
    }),

  uploadOnec: (charges, details, force, only = null) =>
    request('/imports/onec', {
      method: 'POST',
      formData: onecForm(charges, details, force, only),
    }),

  // Идентификаторы должников - третий вид файла (17.09.2026). Паспортов нет ни в
  // одном отчёте, которым грузят начисления: они лежат в другом регистре 1С и
  // выгружаются отдельно. Два обращения, как у остальных загрузок: сперва «кому
  // что ляжет», потом запись.
  previewIdentities: (file, mapping) =>
    request('/imports/identities/preview', {
      method: 'POST',
      formData: importForm(file, { mapping }),
    }),

  uploadIdentities: (file, mapping) =>
    request('/imports/identities', { method: 'POST', formData: importForm(file, { mapping }) }),

  // Сведения о помещениях - пункт 3.8. Площадь, комнаты и этаж печатает
  // финансово-лицевой счёт, приложение к заявлению. Тот же порядок: сначала
  // «какому счёту что ляжет», потом запись по подтверждению.
  previewPremises: (file, mapping) =>
    request('/imports/premises/preview', {
      method: 'POST',
      formData: importForm(file, { mapping }),
    }),

  uploadPremises: (file, mapping) =>
    request('/imports/premises', { method: 'POST', formData: importForm(file, { mapping }) }),

  // Состав зарегистрированных - домовая книга лицевого счёта (30.09.2026). Её
  // печатает выписка из лицевого счёта, приложение к заявлению. Загрузка
  // ЗАМЕНЯЕТ состав счёта целиком, поэтому разбор перед записью здесь не
  // формальность: человек сверяет, кто окажется в листе.
  previewResidents: (file, mapping, only = null) =>
    request('/imports/residents/preview', {
      method: 'POST',
      formData: importForm(file, { mapping, only }),
    }),

  uploadResidents: (file, mapping, only = null) =>
    request('/imports/residents', {
      method: 'POST',
      formData: importForm(file, { mapping, only }),
    }),

  // Файла может не быть вовсе: тогда сведения вносят по одному счёту на его
  // карточке. Пустое поле прежнего значения не стирает - см. `PremisesIn`.
  savePremises: (accountId, values) =>
    request(`/accounts/${accountId}/premises`, { method: 'PATCH', body: values }),

  // Банкротство - §3.7. Процедура принадлежит ЧЕЛОВЕКУ, а не счёту: банкротом
  // признают гражданина, и один должник встречается в нескольких счетах сразу.
  // Отсюда адрес по `personId`, а не по счёту.
  recordBankruptcy: (personId, body) =>
    request(`/persons/${personId}/bankruptcy`, { method: 'POST', body }),

  // Снятие обязано быть возможным: сведения вносит человек и файл - вплоть до
  // однофамильца, - а неснятая ошибка означает навсегда потерянный долг.
  removeBankruptcy: (procedureId) =>
    request(`/bankruptcy/${procedureId}`, { method: 'DELETE' }),

  // Находки сверки с реестром банкротств (вариант А, 27.10.2026): система сама
  // останавливает подачу, а человек подтверждает находку датой принятия заявления
  // либо отклоняет («это не он»). `account_id` - находки людей одного счёта.
  registryFindings: (params) => request('/registry-findings', { params }),
  registryFindingsSummary: () => request('/registry-findings/summary'),
  confirmRegistryFinding: (findingId, body) =>
    request(`/registry-findings/${findingId}/confirm`, { method: 'POST', body }),
  rejectRegistryFinding: (findingId, note) =>
    request(`/registry-findings/${findingId}/reject`, { method: 'POST', body: { note } }),

  // Перечень банкротов файлом. Тот же порядок, что у остальных загрузок, и
  // здесь он важнее прочего: строка этого файла закрывает счёт для подачи.
  previewBankruptcies: (file, mapping) =>
    request('/imports/bankruptcy/preview', {
      method: 'POST',
      formData: importForm(file, { mapping }),
    }),

  uploadBankruptcies: (file, mapping) =>
    request('/imports/bankruptcy', { method: 'POST', formData: importForm(file, { mapping }) }),

  // Поступления - §3.10. Файл тот же путь проходит дважды: сначала «как система
  // поняла колонки», потом запись. Разница с выгрузкой начислений в том, что
  // здесь записываются деньги, и спорную строку система оставляет человеку.
  previewStatement: (file, mapping) =>
    request('/payments/preview', { method: 'POST', formData: importForm(file, { mapping }) }),

  uploadStatement: (file, mapping, source) =>
    request('/payments', {
      method: 'POST',
      formData: importForm(file, { mapping, source }),
    }),

  // Вкладка «Файлы» (решение владельца 30.09.2026): любые файлы в любом
  // порядке, сведения копятся по лицевому счёту, перенос в портфель - кнопкой.
  // С 21.10.2026 список файлов - серверная страница (`items`, `total`) плюс `last_transfer`,
  // `reading` (читается ли хоть один файл) и `revision` (отпечаток всех файлов);
  // `params` - окно и отбор `has_settings`.
  uploads: (params) => request('/uploads', { params }),
  // Сводка минимума - отдельно от списка с 02.10.2026: она долгая, список нет.
  uploadsSummary: () => request('/uploads/summary'),
  sendUploads: (files) => {
    const form = new FormData()
    files.forEach((file) => form.append('files', file))
    return request('/uploads', { method: 'POST', formData: form })
  },
  deleteUpload: (id) => request(`/uploads/${id}`, { method: 'DELETE' }),
  uploadAccounts: (group, params) => request('/uploads/accounts', { params: { ...params, group } }),
  // Счета из окна выбора (16.10.2026) уходят телом; не выбирали - тела нет, и
  // переносятся все готовые.
  transferUploads: (force = false, only = null) =>
    request('/uploads/transfer', {
      method: 'POST',
      params: { force: force ? 'true' : '' },
      ...(only ? { body: { accounts: only } } : {}),
    }),
  uploadChoices: () => request('/uploads/choices'),
  uploadConflicts: () => request('/uploads/conflicts'),
  chooseUploadValue: (accountNumber, fact, keepFileId) =>
    request('/uploads/choice', {
      method: 'POST',
      body: { account_number: accountNumber, fact, keep_file_id: keepFileId },
    }),
  // Реквизиты, срок оплаты и дома с участками, найденные в файле (30.09.2026):
  // сначала показать рядом с настройками, записать - по кнопке человека.
  // Сверка скана (30.09.2026): распознанное идёт в накопитель только после
  // того, как человек сверил поля с картинкой страницы.
  // Расхождения файлов с портфелем (01.10.2026, строка 178): адрес и вид
  // собственности счёта меняет только выбор человека.
  uploadDivergences: () => request('/uploads/divergences'),
  decideDivergences: (factIds, apply) =>
    request('/uploads/divergences', { method: 'POST', body: { fact_ids: factIds, apply } }),
  // Месяцы с начислениями из двух источников (01.10.2026): перенос их не пишет,
  // человек выбирает - оставить записанные, заменить или дописать.
  uploadMonths: () => request('/uploads/months'),
  decideMonths: (ids, action) => request('/uploads/months', { method: 'POST', body: { ids, action } }),
  scanPages: (id) => request(`/uploads/${id}/pages`),
  scanPageImage: (id, number) => `${BASE}/uploads/${id}/pages/${number}/image`,
  decideScanPage: (id, number, decision) =>
    request(`/uploads/${id}/pages/${number}`, { method: 'POST', body: decision }),
  // «Сверить все» (19.10.2026): GET - что подтвердится, для окна; POST - подтвердить.
  scanConfirmPlan: (id) => request(`/uploads/${id}/confirm-all`),
  confirmAllScanPages: (id) => request(`/uploads/${id}/confirm-all`, { method: 'POST' }),
  uploadSettings: (id) => request(`/uploads/${id}/settings`),
  applyUploadSettings: (id, choice) =>
    request(`/uploads/${id}/settings`, { method: 'POST', body: choice }),

  // Серверная страница очереди разбора (21.10.2026): `params` - `limit`, `offset`.
  paymentsReview: (params) => request('/payments/review', { params }),
  matchPayment: (paymentId, accountId) =>
    request(`/payments/${paymentId}/match`, {
      method: 'POST',
      body: { account_id: accountId },
    }),
  reconciliation: (month) => request('/payments/reconciliation', { params: { month } }),

  // 21.10.2026: страницы режет сервер. `params` - окно (`limit`, `offset`) и отбор
  // (`urgency` у радара, `view` у исполнения); числа и суммы плиток приходят по всем.
  radar: (params) => request('/radar', { params }),
  enforcement: (params) => request('/enforcement', { params }),
  // Приказ заводится с карточки счёта: счёт там уже известен, и выбирать его из
  // тысячи чужих строк не приходится. События и заявление об индексации - в
  // разделе «Исполнение», у самого документа.
  registerOrder: (body, asOf) =>
    request('/enforcement/documents', { method: 'POST', body, params: { as_of: asOf } }),
  // Отмена приказа идёт по СЧЁТУ, а не по документу: приказ могли не завести в
  // реестр вовсе (стадию ставят вручную, документ заводят отдельной формой), а
  // счёт в исковую ветку уйти обязан.
  cancelOrder: (body, asOf) =>
    request('/enforcement/order-cancellation', {
      method: 'POST',
      body,
      params: { as_of: asOf },
    }),
  // Исковое заявление (07.10.2026, §6.4.5): сведения об отменённом приказе,
  // суд по умолчанию и деньги иска - одним запросом; правка сведений и сам иск.
  lawsuitForm: (id, asOf) => request(`/accounts/${id}/lawsuit`, { params: { as_of: asOf } }),
  saveCancellation: (id, body, asOf) =>
    request(`/accounts/${id}/order-cancellation`, {
      method: 'PUT',
      body,
      params: { as_of: asOf },
    }),
  buildLawsuit: (id, body, asOf) =>
    request(`/accounts/${id}/lawsuit`, { method: 'POST', body, params: { as_of: asOf } }),
  setEnforcementCourt: (id, courtName, asOf) =>
    request(`/enforcement/documents/${id}/court`, {
      method: 'PATCH',
      body: { court_name: courtName },
      params: { as_of: asOf },
    }),
  recordEnforcementEvent: (id, body, asOf) =>
    request(`/enforcement/documents/${id}/events`, {
      method: 'POST',
      body,
      params: { as_of: asOf },
    }),
  buildIndexation: (id, asOf) =>
    request(`/enforcement/documents/${id}/indexation`, {
      method: 'POST',
      params: { as_of: asOf },
    }),
  channels: () => request('/communications/channels'),
  // Линия связи: доступы к вашему оператору. Пароль обратно не приходит -
  // ответ несёт только признак «задан».
  voiceChannel: () => request('/communications/channels/voice'),
  saveVoiceChannel: (body) => request('/communications/channels/voice', { method: 'POST', body }),
  // Рассылка: доступы к вашему оператору сообщений. Ключ обратно не приходит -
  // ответ несёт только признак «задан», как и пароль линии.
  smsChannel: () => request('/communications/channels/sms'),
  saveSmsChannel: (body) => request('/communications/channels/sms', { method: 'POST', body }),
  // Последние обращения страницей (21.10.2026): `params` - окно (`limit`, `offset`).
  contactHistory: (params) => request('/communications/history', { params }),
  // Зависшие звонки и сообщения: неизвестно, состоялись ли (05.10.2026, С-4).
  // Отметку ставит человек, сверившись с журналом поставщика связи. С 21.10.2026
  // список приходит страницей: `params` - окно (`limit`, `offset`).
  stuckActions: (params) => request('/communications/stuck', { params }),
  markStuckAction: (id, happened) =>
    request(`/communications/stuck/${id}`, { method: 'POST', body: { happened } }),
  // Одно обращение целиком: разговор репликами, путь по шагам, судьба ссылки.
  // Путь собирает сервер - кабинет его не пересобирает и своих подписей не
  // заводит.
  communication: (id) => request(`/communications/${id}`),
  planWave: (accountIds) =>
    request('/communications/wave', { method: 'POST', body: { account_ids: accountIds } }),
  // Звонок одному должнику и обзвон списка (06.10.2026). Правила обращений
  // проверяет сервер и в том, и в другом: отказ приходит словами с «что делать».
  orderCall: (accountId) =>
    request('/communications/calls', { method: 'POST', body: { account_id: accountId } }),
  // Кого обзвонить: должники с телефоном, отметка «обзвон наберёт» и счётчики
  // переключателя. Телефон в строке - тот, что наберётся (`view`: all, pretrial,
  // callable; поиск по номеру счёта или фамилии).
  callList: (params) => request('/communications/debtors', { params }),
  callListSelection: (params) => request('/communications/debtors/selection', { params }),
  startCallWave: (accountIds) =>
    request('/communications/call-waves', { method: 'POST', body: { account_ids: accountIds } }),
  currentCallWave: () => request('/communications/call-waves/current'),
  stopCallWave: (id) => request(`/communications/call-waves/${id}/stop`, { method: 'POST' }),
  // Запись разговора объектом в памяти, а не адресом: адрес с сессией в cookie
  // проигрыватель запросил бы сам, и отказ сервера стал бы немой тишиной.
  recording: (id) => fetchBlob(`/communications/${id}/recording`),
  messageTemplates: () => request('/messages/templates'),
  planMessageWave: (body) => request('/messages/wave/plan', { method: 'POST', body }),
  sendMessageWave: (body) => request('/messages/wave/send', { method: 'POST', body }),
  payback: (since, asOf) => request('/payback', { params: { since, as_of: asOf } }),
  analytics: (asOf) => request('/analytics', { params: { as_of: asOf } }),
  /*
    Портфель в подробностях - вкладка «Подробнее» на «Отчётах». Дата расчёта
    берётся концом периода, как у счётчиков окупаемости на том же экране: иначе
    два блока одной страницы считались бы на разные дни.
  */
  portfolio: (since, until) => request('/portfolio', { params: { since, until, as_of: until } }),
  monthlyReport: (month, asOf) =>
    request('/reports/monthly', { params: { month, as_of: asOf } }),
  downloadMonthlyReport: (month, format) =>
    download('/reports/monthly/export', {
      params: { month, format },
      filename: `arbitr-report-${month}.${format}`,
    }),

  usage: (since, until) => request('/usage', { params: { since, until } }),
  estimateWave: (plan) => request('/usage/estimate', { method: 'POST', body: plan }),

  organisation: () => request('/organisation'),
  saveOrganisation: (body) => request('/organisation', { method: 'PUT', body }),
  requisites: () => request('/organisation/requisites'),
  saveRequisites: (body) => request('/organisation/requisites', { method: 'PUT', body }),
  economics: () => request('/organisation/economics'),
  saveEconomics: (body) => request('/organisation/economics', { method: 'PUT', body }),
  // Условия рассрочки (13.10.2026). Расчёт платежей считает сервер правовым
  // ядром - той же арифметикой, что агент и страница должника.
  instalmentTerms: () => request('/organisation/instalment'),
  saveInstalmentTerms: (body) => request('/organisation/instalment', { method: 'PUT', body }),
  instalmentPreview: (body) =>
    request('/organisation/instalment/preview', { method: 'POST', body }),
  limitationMode: (mode) =>
    request('/organisation/limitation-mode', { method: 'PUT', body: { mode } }),
  // Страница сотрудников (21.10.2026, серверная пагинация): `params` - { limit, offset }.
  users: (params) => request('/users', { params }),
  createUser: (body) => request('/users', { method: 'POST', body }),
  // Ключи обмена с 1С. Значение ключа приходит ТОЛЬКО в ответе на выдачу: в
  // базе лежит хеш, и перечень несёт лишь пометку и отметку об использовании.
  // Страница перечня (21.10.2026, серверная пагинация): `params` - { limit, offset }.
  connectorKeys: (params) => request('/organisation/connector-keys', { params }),
  issueConnectorKey: (note) =>
    request('/organisation/connector-keys', { method: 'POST', body: { note } }),
  revokeConnectorKey: (keyId) =>
    request(`/organisation/connector-keys/${keyId}/revoke`, { method: 'POST' }),
  issueDebtorLink: (accountId, purpose, personId) =>
    request(`/accounts/${accountId}/debtor-link`, {
      method: 'POST',
      params: { purpose, person_id: personId },
    }),
  // Замена ссылки: прежняя закрывается, новая выдаётся тому же получателю.
  // Получателя и способ передачи выбирает сервер по прежней ссылке - кабинет
  // их не передаёт, иначе одно и то же решение принималось бы в двух местах.
  reissueDebtorLink: (accountId, linkId) =>
    request(`/accounts/${accountId}/debtor-links/${linkId}/reissue`, { method: 'POST' }),
  // Страница действующих ссылок (21.10.2026); отбор: person_id, whole_account, replaceable.
  debtorLinks: (accountId, params) => request(`/accounts/${accountId}/debtor-links`, { params }),
  debtorRecipients: (accountId) => request(`/accounts/${accountId}/debtor-recipients`),
  revokeDebtorLink: (accountId, linkId) =>
    request(`/accounts/${accountId}/debtor-links/${linkId}/revoke`, { method: 'POST' }),
}

/*
  Обращения страницы должника.

  Отличий от кабинета два, и оба намеренные. Ссылка предъявляется заголовком, а
  не адресом: значение в пути осело бы в журналах прокси и ушло бы в заголовке
  перехода на чужой сайт. Cookie не отправляются вовсе — этой странице нечего
  предъявлять, кроме ссылки, и случайно захваченная чужая сессия здесь не нужна.
*/
export const LINK_HEADER = 'X-Debtor-Link'

export function debtorApi(token) {
  const options = { headers: { [LINK_HEADER]: token }, credentials: 'omit' }
  return {
    page: () => request('/debtor/page', options),
    previewInstalment: (months, paymentDay) =>
      request('/debtor/instalment/preview', {
        ...options,
        method: 'POST',
        body: { months, payment_day: paymentDay },
      }),
    acceptInstalment: (months, paymentDay) =>
      request('/debtor/instalment', {
        ...options,
        method: 'POST',
        body: { months, payment_day: paymentDay },
      }),
    hardship: (note) =>
      request('/debtor/hardship', { ...options, method: 'POST', body: { note: note || null } }),
    dispute: (note) =>
      request('/debtor/dispute', { ...options, method: 'POST', body: { note: note || null } }),
  }
}
